import * as React from "react";
import { Box, Button, Stack, Typography, useColorScheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import type { NavItemConfig } from "@/types/nav";
import { dashboardConfig } from "@/config/dashboard";
import { goTo, IN_ADMIN } from "@/lib/platform";
import { Page as NotFoundPage } from "@/pages/not-found";

import { useAuth } from "../core/auth-provider";
import { Logo } from "../core/logo";
import { logoColors } from "../dashboard/layout/vertical/side-nav";
import { AccountPicker } from "./account-picker";

export interface AuthGuardProps {
	children: React.ReactNode;
}

/**
 * Every navigable item in the sidebar, at any depth.
 *
 * This used to reach exactly two levels, which was enough while the config was flat. Admin
 * Settings nests Plugins one level deeper, and at three levels a hand-rolled flatten silently
 * stops finding the item — so `/plugins` would keep its sidebar gating and quietly lose its
 * route gating. Recursion is what keeps the two in step whatever the config does next.
 *
 * `/` is skipped deliberately: it prefixes every path, so treating it as a gated route would
 * apply the dashboard's own requirements to the whole app.
 */
function flattenNavItems(items: NavItemConfig[]): NavItemConfig[] {
	return items.flatMap((item) => [
		...(item.href && item.href !== "/" ? [item] : []),
		...flattenNavItems(item.items ?? []),
	]);
}

export function AuthGuard({ children }: AuthGuardProps): React.JSX.Element | null {
	const { isLoading, hasLoadError, getMe, isAuthenticated, setUser, account, user, permissions, setPermissions } =
		useAuth();
	const pathname = useLocation();

	const { colorScheme = "light" } = useColorScheme();

	const logoColor = logoColors[colorScheme]["blend_in"];

	const navigate = useNavigate();

	React.useEffect(() => {
		if (!localStorage.getItem("token")) {
			setUser(null);
			setPermissions({});
			navigate("/sign-in");

			return;
		}

		if (isLoading || isAuthenticated || localStorage.getItem("token")) {
			return;
		}

		setUser(null);
		setPermissions({});
		navigate("/sign-in");
	}, [isAuthenticated, isLoading, navigate, pathname, setPermissions, setUser]);

	// The panel is for operators and enterprise accounts; a business belongs in its own app.
	const isBusiness = IN_ADMIN && isAuthenticated && account?.type === "smb" && !user?.isSuperAdmin;
	React.useEffect(() => {
		if (isBusiness) goTo(navigate, "smb", "/");
	}, [isBusiness, navigate]);

	if (hasLoadError) {
		// The session is still valid but /me failed (network down, backend deploying). Retrying is the
		// usual way out; signing out stays available for a profile that keeps failing to load.
		const signOut = () => {
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			navigate("/sign-in");
		};

		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 4,
					p: 2,
				}}
			>
				<Logo height={60} width={222} color={logoColor} />
				<Stack spacing={2} sx={{ alignItems: "center", maxWidth: 360, textAlign: "center" }}>
					<Typography>We couldn’t load your profile. Check your connection and try again.</Typography>
					<Stack direction="row" spacing={1}>
						<Button variant="contained" onClick={() => getMe()}>
							Try again
						</Button>
						<Button variant="text" onClick={signOut}>
							Sign out
						</Button>
					</Stack>
				</Stack>
			</Box>
		);
	}

	if (isLoading || !isAuthenticated || isBusiness) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 4,
				}}
			>
				<Logo height={60} width={222} color={logoColor} />
				<Box className="page-loader" />
			</Box>
		);
	}

	const navItems = dashboardConfig.navItems?.[0]?.items ?? [];
	const allNavItems = flattenNavItems(navItems);

	const navItemsWithPermissions = allNavItems.filter((item) => item.permission);
	const navItemsWithNeedAccount = allNavItems.filter((item) => item.needAccount).map((item) => item.href);
	const navItemsWithIsSuperAdmin = allNavItems.filter((item) => item.isSuperAdmin).map((item) => item.href);

	const needsAccount =
		navItemsWithNeedAccount.some((route) => route && pathname.pathname.startsWith(route)) || pathname.pathname === "/";

	const isSuperAdmin = user?.isSuperAdmin;
	const needsSuperAdmin = navItemsWithIsSuperAdmin.some((route) => route && pathname.pathname.startsWith(route));

	const currentPermissionNavItem = navItemsWithPermissions.find(
		(item) => item?.href && pathname.pathname.startsWith(item.href)
	);
	const needsPermission =
		currentPermissionNavItem?.permission && !permissions[currentPermissionNavItem?.permission ?? ""];

	if ((pathname.pathname === "/accounts" || pathname.pathname === "/") && !isSuperAdmin && !account) {
		if (pathname.pathname !== "/") {
			navigate("/", { replace: true });
			return null;
		}
		return <AccountPicker />;
	}

  if (!account && isSuperAdmin && pathname.pathname === "/") {
    navigate("/accounts", { replace: true });
    return null;
  }

	if ((!account && needsAccount) || (!isSuperAdmin && needsSuperAdmin) || needsPermission) {
		return <NotFoundPage />;
	}

	return <React.Fragment>{children}</React.Fragment>;
}
